import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  Box,
  GDSStyleObject,
  HeadingText,
  FormField,
  Radio,
  TextInput,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';
import COBQuestioner from '../../../../assets/json/COB_QUESTIONER.json';
import {
  isEmpty,
  compareTwoDate,
  isNoSpace,
  isNotEmpty,
  isValidBin,
  isNumberOnly,
  isAlphaNumberic,
  getPCNBINValidated,
} from '../../../helpers/helpers';
import { Calendar } from '../../../reusable-components/calendar/calendar';
import useCobStore from '../../../../store/cob-store';
import '../../forms.css';
// eslint-disable-next-line import/order
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { cobLibrary } from '../../../../cob-library.config';
import { COBLayout } from '../../../cob-layout/cob-layout';

const intialValue = {
  partDCarrierName: '',
  partDPolicyNumber: '',
  partDGroupNumber: '',
  bin: '',
  pcn: '',
  partDEffectiveDate: '',
  partDCancelDate: '',
};

export const MedicarePartD = forwardRef((props: any, ref) => {
  const { OPISResponseInfo, nextDisabled, errorHandling, isValidPCN } = props;

  const dependantList = useCobStore((state: any) => state.demographics);
  const isBelow400 = useMediaQuery(queryHelpers.down(400));
  const isBelow380 = useMediaQuery(queryHelpers.down(380));
  const [hasMedicarePartD, setHasMedicarePartD] = useState(false);
  const [isTerminationDateSelected, setIsTerminationDateSelected] =
    useState<boolean>(false);
  const [isCheckBoxSelected, setIsChecboxSelected] = useState<boolean>(false);
  const [effectiveDateError, setEffectiveDateError] = useState<string>('');
  const [terminationDateError, setTerminationDateError] = useState<string>('');
  const [isBinNumberError, setBinNumberError] = useState<string>('');
  const [form, setForm] = useState(intialValue);
  const [errors, setErrors] = useState({
    partDEffectiveDate: false,
    partDCancelDate: false,
    bin: false,
  });
  const binServerError = localStorage.getItem('pcnError');

  const effectiveDate =
    form.partDEffectiveDate || OPISResponseInfo.partDEffectiveDate;
  const terminationDate =
    form.partDCancelDate || OPISResponseInfo.partDCancelDate;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const config = cobLibrary.getConfig();
  const { HIGHMARK_API_URL, useCurrentRegionUbkHeader } = config;
  const currentUbk: any = useCurrentRegionUbkHeader() || null;
  const ubkValue = currentUbk?.headers?.Brandubk || null;
  const pcnBinURL = `${HIGHMARK_API_URL}/digital/v2/members/coordination-of-benefits/pcn-bin?pcn=`;
  const OPISDependantName = OPISResponseInfo.member.firstName;

  const formField =
    isEmpty(effectiveDate) ||
    (isTerminationDateSelected && isEmpty(form.partDCancelDate)) ||
    compareTwoDate(effectiveDate, terminationDate) ||
    (isNotEmpty(form.pcn) && (isEmpty(form.bin) || !isValidBin(form.bin))) ||
    (isNotEmpty(form.bin) && !isValidBin(form.bin));

  useEffect(() => {
    if (hasMedicarePartD && formField) {
      errorHandling(false);
    } else {
      errorHandling(true);
    }
  }, [errorHandling, formField, hasMedicarePartD]);

  useImperativeHandle(ref, () => ({
    callValidation() {
      if (isEmpty(effectiveDate)) {
        setEffectiveDateError(COBQuestioner.COB.ERRORS.REQUIREDFIELD);
        setErrors((prevState) => ({
          ...prevState,
          partDEffectiveDate: true,
        }));
      } else {
        setEffectiveDateError('');
        setErrors((prevState) => ({
          ...prevState,
          partDEffectiveDate: false,
        }));
      }

      if (isTerminationDateSelected || form.partDCancelDate) {
        if (isEmpty(terminationDate)) {
          setTerminationDateError(COBQuestioner.COB.ERRORS.VALIDDATE);
          setErrors((prevState) => ({
            ...prevState,
            partDCancelDate: true,
          }));
        } else if (compareTwoDate(effectiveDate, terminationDate)) {
          setTerminationDateError(COBQuestioner.COB.ERRORS.FUTUREDATE);
          setErrors((prevState) => ({
            ...prevState,
            partDCancelDate: true,
          }));
        } else {
          setTerminationDateError('');
          setErrors((prevState) => ({
            ...prevState,
            partDCancelDate: false,
          }));
        }
      }

      if (isNotEmpty(form.bin) && !isValidBin(form.bin)) {
        setBinNumberError(COBQuestioner.COB.ERRORS.BINLENGTH);
        localStorage.setItem('pcnError', '');
        setErrors((prevState) => ({
          ...prevState,
          bin: true,
        }));
      } else if (
        localStorage.getItem('pcnError') !== '' &&
        localStorage.getItem('pcnError') !== null
      ) {
        // const localStorageError = localStorage.getItem('pcnError');
        setBinNumberError(COBQuestioner.COB.ERRORS.BINSERVERERROR);
        setErrors((prevState) => ({
          ...prevState,
          bin: true,
        }));
      } else {
        setBinNumberError('');
        setErrors((prevState) => ({
          ...prevState,
          bin: false,
        }));
      }
    },
    checkPCNBIN() {
      if (isValidBin(form.bin) && isNotEmpty(form.pcn)) {
        setIsLoading(true);
        const payload = { payload: { bin: form.bin, pcn: form.pcn } };
        getPCNBINValidated(pcnBinURL, payload, ubkValue).then((result: any) => {
          const validatedBIN = result?.validPcnBin;
          if (!validatedBIN) {
            localStorage.setItem(OPISDependantName, validatedBIN);
            setBinNumberError(COBQuestioner.COB.ERRORS.BINSERVERERROR);
            setIsLoading(false);
            errorHandling(true);
            isValidPCN(true);
            localStorage.setItem('pcnValidationSuccess', 'false');
            setErrors((prevState) => ({
              ...prevState,
              bin: true,
            }));
          } else {
            localStorage.setItem(OPISDependantName, validatedBIN);
            setBinNumberError('');
            setIsLoading(false);
            errorHandling(false);
            isValidPCN(false);
            localStorage.setItem('pcnError', '');
            localStorage.setItem('pcnValidationSuccess', 'true');
            setErrors((prevState) => ({
              ...prevState,
              bin: false,
            }));
          }
        });
      } else {
        localStorage.setItem('pcnValidationSuccess', 'true');
        isValidPCN(false);
      }
    },
    getDependant() {
      return OPISDependantName;
    },
  }));
  useEffect(() => {
    if (
      hasMedicarePartD ||
      isCheckBoxSelected ||
      OPISResponseInfo.partDCoverage !== ''
    ) {
      nextDisabled(false);
    } else {
      nextDisabled(true);
    }
  }, [
    nextDisabled,
    hasMedicarePartD,
    isCheckBoxSelected,
    OPISResponseInfo.partDCoverage,
  ]);

  useEffect(() => {
    if (OPISResponseInfo?.partDCoverage === 'Y') {
      setHasMedicarePartD(true);
    } else {
      setHasMedicarePartD(false);
    }
  }, [OPISResponseInfo?.partDCoverage, hasMedicarePartD]);

  const updatePartDHandler = (event: any) => {
    OPISResponseInfo.partDCoverage = event.target.value;
    if (event.target.value === 'Y') {
      setHasMedicarePartD(true);
      localStorage.setItem('pcnError', '');
      setBinNumberError('');
      setErrors((prevState) => ({
        ...prevState,
        bin: false,
      }));
    } else {
      setHasMedicarePartD(false);
    }
    setIsChecboxSelected(true);
    setErrors((prevState) => ({
      ...prevState,
      partDEffectiveDate: false,
      partDCancelDate: false,
    }));
    OPISResponseInfo.partDEffectiveDate = '';
    OPISResponseInfo.partDCancelDate = '';
    form.partDEffectiveDate = '';
    form.partDCancelDate = '';
  };

  const partDEffectiveDate = (selectedEffectiveDate: string) => {
    setForm((prevState) => ({
      ...prevState,
      partDEffectiveDate: selectedEffectiveDate,
    }));
    form.partDEffectiveDate = selectedEffectiveDate;
    OPISResponseInfo.partDEffectiveDate = selectedEffectiveDate;
    setErrors((prevState) => ({
      ...prevState,
      partDEffectiveDate: !selectedEffectiveDate,
    }));
  };

  const partDCancelDate = (selectedTerminationDate: string) => {
    setForm((prevState) => ({
      ...prevState,
      partDCancelDate: selectedTerminationDate,
    }));
    form.partDCancelDate = selectedTerminationDate;
    OPISResponseInfo.partDCancelDate = selectedTerminationDate;
    setErrors((prevState) => ({
      ...prevState,
      partDCancelDate: !selectedTerminationDate,
    }));
  };

  useEffect(() => {
    if (isEmpty(form.bin)) {
      setBinNumberError('');
      setErrors((prevState) => ({
        ...prevState,
        bin: false,
      }));
    }
    if (
      localStorage.getItem('pcnError') !== '' &&
      localStorage.getItem('pcnError') !== null
    ) {
      // const localStorageError = localStorage.getItem('pcnError');
      setBinNumberError(COBQuestioner.COB.ERRORS.BINSERVERERROR);
      setErrors((prevState) => ({
        ...prevState,
        bin: true,
      }));
    }
  }, [form.bin, isBinNumberError]);

  useEffect(() => {
    if (isNotEmpty(form.pcn) && isEmpty(form.bin)) {
      setBinNumberError(COBQuestioner.COB.ERRORS.REQUIREDFIELD);
      setErrors((prevState) => ({
        ...prevState,
        bin: true,
      }));
    } else if (isEmpty(form.pcn) && isEmpty(form.bin)) {
      setBinNumberError('');
      setErrors((prevState) => ({
        ...prevState,
        bin: false,
      }));
    } else if (
      localStorage.getItem('pcnError') !== '' &&
      localStorage.getItem('pcnError') !== null
    ) {
      // const localStorageError = localStorage.getItem('pcnError');
      setBinNumberError(COBQuestioner.COB.ERRORS.BINSERVERERROR);
      setErrors((prevState) => ({
        ...prevState,
        bin: true,
      }));
    }
  }, [form.pcn, form.bin, isBinNumberError, binServerError]);

  const onUpdateField = (event: any) => {
    const { value, id } = event.target;
    const nextFormState = {
      ...form,
      [id]: value,
    };
    setForm(nextFormState);

    if (id === 'partCPolicyNumber') {
      setForm((prevState) => ({
        ...prevState,
        partCPolicyNumber: isNoSpace(value) ? '' : isAlphaNumberic(value),
      }));
    }

    if (id === 'partCGroupNumber') {
      setForm((prevState) => ({
        ...prevState,
        partCGroupNumber: isNoSpace(value) ? '' : isAlphaNumberic(value),
      }));
    }

    if (id === 'bin') {
      localStorage.setItem(OPISDependantName, '');
      localStorage.setItem('pcnError', '');
      isValidPCN(true);
      setForm((prevState) => ({
        ...prevState,
        bin: isNoSpace(value) ? '' : isNumberOnly(value),
      }));
    }

    if (id === 'pcn') {
      localStorage.setItem(OPISDependantName, '');
      isValidPCN(true);
      setForm((prevState) => ({
        ...prevState,
        pcn: isNoSpace(value) ? '' : isAlphaNumberic(value),
      }));
    }

    if (isNotEmpty(form.pcn) && isEmpty(form.bin)) {
      setBinNumberError(COBQuestioner.COB.ERRORS.REQUIREDFIELD);
      setErrors((prevState) => ({
        ...prevState,
        bin: true,
      }));
    } else if (
      localStorage.getItem('pcnError') !== '' &&
      localStorage.getItem('pcnError') !== null
    ) {
      // const localStorageError = localStorage.getItem('pcnError');
      setBinNumberError(COBQuestioner.COB.ERRORS.BINSERVERERROR);
      setErrors((prevState) => ({
        ...prevState,
        bin: true,
      }));
    } else {
      setBinNumberError('');
      setErrors((prevState) => ({
        ...prevState,
        bin: false,
      }));
    }

    OPISResponseInfo.partDCarrierName = nextFormState.partDCarrierName;
    OPISResponseInfo.partDPolicyNumber = nextFormState.partDPolicyNumber;
    OPISResponseInfo.partDGroupNumber = nextFormState.partDGroupNumber;
    OPISResponseInfo.bin = nextFormState.bin;
    OPISResponseInfo.pcn = nextFormState.pcn;
    OPISResponseInfo.partDEffectiveDate = nextFormState.partDEffectiveDate;
    OPISResponseInfo.partDCancelDate = nextFormState.partDCancelDate;
  };

  useEffect(() => {
    if (
      OPISResponseInfo.partDCarrierName ||
      OPISResponseInfo.partDPolicyNumber ||
      OPISResponseInfo.partDGroupNumber ||
      OPISResponseInfo.partDEffectiveDate ||
      OPISResponseInfo.partDCancelDate ||
      OPISResponseInfo.bin ||
      OPISResponseInfo.pcn
    ) {
      form.partDCarrierName = OPISResponseInfo.partDCarrierName;
      form.partDPolicyNumber = OPISResponseInfo.partDPolicyNumber;
      form.partDGroupNumber = OPISResponseInfo.partDGroupNumber;
      form.partDEffectiveDate = OPISResponseInfo.partDEffectiveDate;
      form.partDCancelDate = OPISResponseInfo.partDCancelDate;
      form.bin = OPISResponseInfo.bin;
      form.pcn = OPISResponseInfo.pcn;
    }
  }, [OPISResponseInfo, form]);

  return (
    <COBLayout
      title={
        OPISResponseInfo?.member?.firstName ===
          dependantList.demographics.firstName &&
        OPISResponseInfo.member.dateOfBirth ===
          dependantList?.demographics?.dateOfBirth
          ? COBQuestioner.COB.INFORMATION.YOURMEDICARE
          : `${`${OPISResponseInfo.member.firstName} ${OPISResponseInfo.member.lastName}${COBQuestioner.COB.INFORMATION.DEPENDENTMEDICARE}`}`
      }
      dataTestId="medicare_partD"
      questionTitle={
        OPISResponseInfo?.member?.firstName ===
        dependantList.demographics.firstName
          ? COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.TITLE
          : `${`${COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.DEPENDENTTITLE}${OPISResponseInfo.member.firstName}${COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.CONTINUOUSDEPENDANTTITLE}`}`
      }
      disclaimer={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.HOSPITALINSURANCE}
      isDependant={false}
    >
      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            zIndex: '1002',
          } as GDSStyleObject
        }
      >
        {isLoading && <LoadingIndicator />}
      </Box>
      <Box
        css={{
          width: isBelow380 ? '332px' : '368px',
        }}
      >
        <Box className="GDS-radio-warapper marginBottom15">
          <Radio
            id="medicare_partD_Yes"
            name="partDCoverage"
            data-testid="medicare_PartD_Yes"
            onChange={(e) => {
              updatePartDHandler(e);
            }}
            label="Yes"
            layout="row"
            checked={OPISResponseInfo?.partDCoverage === 'Y'}
            value={
              COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.ISPARTD.YES ? 'Y' : ''
            }
          />
        </Box>
        <Box className="GDS-radio-warapper marginBottom15">
          <Radio
            id="medicare_partD_No"
            name="partDCoverage"
            data-testid="medicare_PartD_No"
            onChange={(e) => {
              updatePartDHandler(e);
            }}
            label="No"
            layout="row"
            checked={OPISResponseInfo?.partDCoverage === 'N'}
            value={
              COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.ISPARTD.NO ? 'N' : ''
            }
          />
        </Box>
        {hasMedicarePartD && (
          <Box>
            <HeadingText
              level="3"
              size={{
                '@initial': 'xl',
                '@mobile': 'xl',
                '@mobileLandscape': 'xl',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
              css={
                { marginTop: '$half', marginBottom: '$one' } as GDSStyleObject
              }
            >
              {' '}
              {COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.SUBTITLE}
            </HeadingText>
            <FormField
              label={
                COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.INSURANCECOMPANY
              }
              id="partDCarrierName"
              css={
                {
                  marginBottom: '$threeQuarters',
                  '& label': {
                    marginBottom: '$half',
                  },
                } as GDSStyleObject
              }
            >
              <TextInput
                placeholder={
                  COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD
                    .PLACEHOLDERCOMPANYNAME
                }
                name="partDCarrierName"
                id="partDCarrierName"
                value={form.partDCarrierName}
                maxLength={35}
                onChange={onUpdateField}
              />
            </FormField>
            <FormField
              label={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.POLICYNUMBER}
              id="partDPolicyNumber"
              css={
                {
                  marginBottom: '$threeQuarters',
                  '& label': {
                    marginBottom: '$half',
                  },
                } as GDSStyleObject
              }
            >
              <TextInput
                placeholder={
                  COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD
                    .PLACEHOLDERPOLICYNUMBER
                }
                name="partDPolicyNumber"
                id="partDPolicyNumber"
                value={form.partDPolicyNumber}
                maxLength={17}
                onChange={onUpdateField}
              />
            </FormField>
            <FormField
              label={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.GROUPNUMBER}
              id="partDGroupNumber"
              css={
                {
                  marginBottom: '$threeQuarters',
                  '& label': {
                    marginBottom: '$half',
                  },
                } as GDSStyleObject
              }
            >
              <TextInput
                placeholder={
                  COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD
                    .PLACEHOLDERGROUPNUMBER
                }
                name="partDGroupNumber"
                id="partDGroupNumber"
                value={form.partDGroupNumber}
                maxLength={30}
                onChange={onUpdateField}
                pattern="[0-9]*"
              />
            </FormField>
            <FormField
              label={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.BIN}
              id="bin"
              inputStatus={errors.bin ? 'error' : undefined}
              statusMessage={errors.bin ? isBinNumberError : ''}
              css={
                {
                  marginBottom: '$threeQuarters',
                  '& label': {
                    marginBottom: '$half',
                  },
                } as GDSStyleObject
              }
            >
              <TextInput
                placeholder={
                  COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.PLACEHOLDERBIN
                }
                name="bin"
                id="bin"
                value={form.bin}
                maxLength={6}
                minLength={6}
                onChange={onUpdateField}
              />
            </FormField>
            <FormField
              label={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.PCN}
              id="pcn"
              css={
                {
                  marginBottom: '$threeQuarters',
                  '& label': {
                    marginBottom: '$half',
                  },
                } as GDSStyleObject
              }
            >
              <TextInput
                placeholder={
                  COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.PLACEHOLDERPCN
                }
                name="pcn"
                id="pcn"
                value={form.pcn}
                maxLength={10}
                onChange={onUpdateField}
              />
            </FormField>
            <FormField
              id="partDEffectiveDate"
              label={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.EFFECTIVEDATE}
              aria-label={
                COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.EFFECTIVEDATE
              }
              css={
                {
                  marginBottom: '$threeQuarters',
                  '& label': {
                    marginBottom: '$half',
                  },
                } as GDSStyleObject
              }
            >
              <Calendar
                selectedFormatDate={partDEffectiveDate}
                currentDate={form.partDEffectiveDate}
                showError={errors.partDEffectiveDate}
                errorMessage={effectiveDateError}
                setWidth={isBelow400 ? '105px' : '110px'}
              />
            </FormField>
            <FormField
              id="partDCancelDate"
              label={COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.TERMINATIONDATE}
              aria-label={
                COBQuestioner.COB.QUESTIONS.CONTROLS.PARTD.TERMINATIONDATE
              }
              css={
                {
                  '& label': {
                    marginBottom: '$half',
                  },
                } as GDSStyleObject
              }
            >
              <Calendar
                selectedFormatDate={partDCancelDate}
                currentDate={form.partDCancelDate}
                showError={errors.partDCancelDate}
                errorMessage={terminationDateError}
                selectedDataValue={(val: any) =>
                  setIsTerminationDateSelected(val)
                }
                setWidth={isBelow400 ? '105px' : '110px'}
              />
            </FormField>
          </Box>
        )}
      </Box>
    </COBLayout>
  );
});
